/**
 * MasterTimelineBar — horizontal SVG timeline for a single judgment.
 *
 * Consumes the /api/judgment-law/timeline response (same shape used by the
 * Phase 9B JudgmentInsightPanel). Plots markers for:
 *   - Judgment expiration
 *   - Renewal deadline
 *   - Real property lien expiration (distinct when lien_differs_from_judgment)
 *   - Dormancy trigger
 *   - Idaho dual clocks (10y enforcement / 11y action-on-judgment)
 *   - Florida two tranche markers
 *   - TODAY vertical line
 *
 * Hover any marker → tooltip with date + days remaining + short description.
 */
import React, { useMemo, useState } from "react";
import { CalendarClock, FileText, Flag, LandPlot, Clock, ShieldAlert } from "lucide-react";
import { useJudgmentTimeline } from "@/hooks/useJudgmentLaw";
import JeaCard from "@/components/jea/JeaCard";
import { cn } from "@/lib/utils";
import { RENEWAL_MODE, resolveRenewalDisplay } from "@/lib/renewalDisplay";

const parseISO = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).getTime();
  } catch {
    return null;
  }
};

const urgencyColor = (daysUntil, isExpired) => {
  if (isExpired) return "#5A7A9A"; // grey
  if (daysUntil == null) return "#A8B8D0";
  if (daysUntil < 90) return "#E74C3C"; // red (< 90d)
  if (daysUntil < 365) return "#F39C12"; // amber
  if (daysUntil < 730) return "#F1C40F"; // yellow
  return "#2ECC71"; // green
};

const shortDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
};

// Compute all markers from the timeline response.
const buildMarkers = (data) => {
  const dates = data?.dates || {};
  const markers = [];
  const jExp = dates.judgment_expires || {};
  const rDeadline = dates.renewal_deadline || {};
  const dTrigger = dates.dormancy_trigger || {};
  const lExp = dates.lien_expires || {};
  const entered = dates.judgment_entered || {};
  // Phase 9G — admin-marked NO_PATHWAY states hide renewal + dormancy markers
  // and strip the "Renewal via ..." suffix on the expiration marker.
  const noPathway = resolveRenewalDisplay(data) === RENEWAL_MODE.NO_PATHWAY;

  if (entered.iso) {
    markers.push({
      key: "entered",
      iso: entered.iso,
      days_until: entered.days_until,
      label: "Judgment Entered",
      Icon: CalendarClock,
      color: "#A8B8D0",
      description: `Judgment entered ${shortDate(entered.iso)}.`,
    });
  }
  if (rDeadline.iso && !data?.hard_cutoff && !noPathway) {
    markers.push({
      key: "renewal",
      iso: rDeadline.iso,
      days_until: rDeadline.days_until,
      label: "Renewal Deadline",
      Icon: Flag,
      color: urgencyColor(rDeadline.days_until, false),
      description: `Renewal mechanism: ${data?.renewal_mechanism || "—"}.`,
    });
  }
  if (dTrigger.iso && !noPathway) {
    markers.push({
      key: "dormancy",
      iso: dTrigger.iso,
      days_until: dTrigger.days_until,
      label: "Dormancy Trigger",
      Icon: Clock,
      color: urgencyColor(dTrigger.days_until, false),
      description: `Judgment becomes dormant. Revival mechanism: ${data?.renewal_mechanism || "—"}.`,
    });
  }
  if (jExp.iso) {
    markers.push({
      key: "j-expires",
      iso: jExp.iso,
      days_until: jExp.days_until,
      label: "Judgment Expires",
      Icon: ShieldAlert,
      color: urgencyColor(jExp.days_until, data?.expired),
      description: noPathway
        ? "No renewal pathway available in this jurisdiction."
        : data?.hard_cutoff
        ? "Hard cutoff — no renewal available."
        : `Judgment expires. Renewal via ${data?.renewal_mechanism || "—"}.`,
    });
  }
  if (lExp.iso && (data?.lien_differs_from_judgment || data?.lien_filed)) {
    markers.push({
      key: "lien-expires",
      iso: lExp.iso,
      days_until: lExp.days_until,
      label: data?.variant_key === "FL" ? "Tranche 1 Ends" : "Lien Expires",
      Icon: LandPlot,
      color: urgencyColor(lExp.days_until, false),
      description: data?.lien_life_note || "Re-record per state rules.",
    });
  }

  // ---- Variant-specific extra markers ----
  const enteredMs = parseISO(entered.iso);
  const addYearsIso = (years) => {
    if (!enteredMs) return null;
    const d = new Date(enteredMs);
    d.setFullYear(d.getFullYear() + years);
    return { iso: d.toISOString().slice(0, 10), days_until: Math.round((d.getTime() - Date.now()) / 86400000) };
  };
  const key = data?.variant_key;

  // Idaho — TWO clocks: 10y enforcement + 11y action-on-judgment.
  if (key === "ID") {
    const eleven = addYearsIso(11);
    if (eleven) {
      markers.push({
        key: "id-action-on-judgment",
        iso: eleven.iso,
        days_until: eleven.days_until,
        label: "11y Action-on-Judgment",
        Icon: FileText,
        color: urgencyColor(eleven.days_until, false),
        description: "Idaho — separate 11-year action-on-judgment clock. Track alongside 10y enforcement.",
      });
    }
  }
  // Florida — Tranche 2 = 20y from entry (second 10y tranche of lien).
  if (key === "FL") {
    const twenty = addYearsIso(20);
    if (twenty) {
      markers.push({
        key: "fl-tranche-2",
        iso: twenty.iso,
        days_until: twenty.days_until,
        label: "Tranche 2 (re-record)",
        Icon: LandPlot,
        color: urgencyColor(twenty.days_until, false),
        description: "Florida — must re-record lien to preserve the second 10-year tranche.",
      });
    }
  }
  // Illinois — 20y revival outer limit
  if (key === "IL") {
    const twenty = addYearsIso(20);
    if (twenty) {
      markers.push({
        key: "il-revival-outer",
        iso: twenty.iso,
        days_until: twenty.days_until,
        label: "Revival Outer Limit",
        Icon: FileText,
        color: urgencyColor(twenty.days_until, false),
        description: "Illinois — revival petition must be filed within 20 years of entry.",
      });
    }
  }
  // NY / WI re-docket = 10y from entry (already captured by lien_expires).
  return markers;
};


export const MasterTimelineBar = ({ payload, testid = "master-timeline-bar" }) => {
  const { data, loading, error } = useJudgmentTimeline(payload);
  const [hover, setHover] = useState(null);

  const markers = useMemo(() => (data ? buildMarkers(data) : []), [data]);

  const { minMs, maxMs, todayX } = useMemo(() => {
    if (!markers.length) return { minMs: null, maxMs: null, todayX: null };
    const times = markers.map((m) => parseISO(m.iso)).filter(Boolean);
    const now = Date.now();
    const min = Math.min(now, ...times);
    const max = Math.max(now, ...times);
    // Pad both sides by 3% so end markers don't clip the frame.
    const span = max - min || 86400000;
    const pad = span * 0.05;
    return { minMs: min - pad, maxMs: max + pad, todayX: now };
  }, [markers]);

  const width = 1200;
  const height = 120;
  const trackY = 60;

  const xFor = (ms) => {
    if (!minMs || !maxMs) return 0;
    return ((ms - minMs) / (maxMs - minMs)) * (width - 40) + 20;
  };

  if (loading) {
    return (
      <JeaCard className="min-h-[160px] flex items-center justify-center animate-pulse" data-testid={`${testid}-loading`}>
        <div className="text-text-secondary text-[13px]">Loading timeline…</div>
      </JeaCard>
    );
  }
  if (error || !data?.known) return null;
  if (!markers.length) return null;

  return (
    <JeaCard data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="jea-caption">Master Timeline</div>
        <div className="text-[12px] text-text-secondary">
          {data.name} · Duration {data.duration_note}
        </div>
      </div>
      <div className="relative">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-[140px]">
          {/* Track */}
          <line
            x1={20}
            x2={width - 20}
            y1={trackY}
            y2={trackY}
            stroke="#1E3A58"
            strokeWidth={2}
          />
          {/* TODAY line */}
          {todayX && (
            <>
              <line
                x1={xFor(todayX)}
                x2={xFor(todayX)}
                y1={trackY - 30}
                y2={trackY + 30}
                stroke="#fb6f92"
                strokeDasharray="4 3"
                strokeWidth={1.5}
              />
              <text x={xFor(todayX) + 4} y={trackY - 34} fontSize={10} fill="#fb6f92" fontWeight="700">
                TODAY
              </text>
            </>
          )}
          {/* Markers */}
          {markers.map((m, i) => {
            const ms = parseISO(m.iso);
            if (!ms) return null;
            const x = xFor(ms);
            const isHover = hover?.key === m.key;
            return (
              <g
                key={m.key}
                transform={`translate(${x}, ${trackY})`}
                onMouseEnter={() => setHover(m)}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: "pointer" }}
                data-testid={`timeline-marker-${m.key}`}
              >
                <line x1={0} x2={0} y1={-20} y2={20} stroke={m.color} strokeWidth={2} />
                <circle
                  cx={0}
                  cy={0}
                  r={isHover ? 10 : 7}
                  fill={m.color}
                  stroke="#0D1E36"
                  strokeWidth={2}
                />
                <text
                  x={0}
                  y={40}
                  fontSize={10}
                  fill="#FFFFFF"
                  textAnchor="middle"
                  fontWeight="600"
                  style={{ pointerEvents: "none" }}
                >
                  {m.label}
                </text>
                <text
                  x={0}
                  y={53}
                  fontSize={9}
                  fill="#A8B8D0"
                  textAnchor="middle"
                  style={{ pointerEvents: "none" }}
                >
                  {shortDate(m.iso)}
                </text>
              </g>
            );
          })}
        </svg>
        {hover && (
          <div
            className="absolute top-2 right-2 max-w-[280px] rounded-[8px] bg-bg-tertiary border border-border-default p-3 text-[12px] shadow-elevated"
            data-testid="timeline-tooltip"
          >
            <div className="text-white font-bold flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: hover.color }} />
              {hover.label}
            </div>
            <div className="text-text-secondary mt-1 jea-mono">
              {shortDate(hover.iso)}{" "}
              {hover.days_until != null && (
                <span className="text-white">· {hover.days_until}d</span>
              )}
            </div>
            <div className="text-text-secondary mt-1.5">{hover.description}</div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-3 text-[11px] text-text-secondary">
        {[
          { label: "Safe (>2y)", color: "#2ECC71" },
          { label: "Approaching", color: "#F1C40F" },
          { label: "Warning (<1y)", color: "#F39C12" },
          { label: "Urgent (<90d)", color: "#E74C3C" },
          { label: "Expired", color: "#5A7A9A" },
        ].map((entry) => (
          <div key={entry.label} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: entry.color }} />
            {entry.label}
          </div>
        ))}
      </div>
    </JeaCard>
  );
};

export default MasterTimelineBar;
